/**
 * @file PriceList.jsx
 * @description Modern pricing component with Tailwind CSS
 */

import React from 'react';
import { pricingData } from '../data/pricing';

const PriceList = ({ title = 'Our Pricing', showNote = true }) => {
  const categories = [
    { name: 'Standard Services', data: pricingData.standard, color: 'blue' },
    { name: 'Evening Services (6pm-10pm)', data: pricingData.evening, color: 'purple' },
    { name: 'Weekend Services', data: pricingData.weekend, color: 'green' },
    { name: 'Emergency Services', data: pricingData.emergency, color: 'red' },
  ];

  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600',
    red: 'from-red-500 to-red-600',
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{title}</h2>
          {showNote && (
            <div className="inline-flex items-center space-x-2 bg-yellow-50 border border-yellow-200 text-yellow-800 px-6 py-3 rounded-lg">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Zone 1 services incur an additional £30 charge on all prices</span>
            </div>
          )}
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {categories.map((category, index) => (
            <div
              key={category.name}
              className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Category Header */}
              <div className={`bg-gradient-to-r ${colorClasses[category.color]} text-white px-6 py-4`}>
                <h3 className="text-xl font-bold">{category.name}</h3>
              </div>

              {/* Pricing Table */}
              <div className="p-6">
                <div className="space-y-4">
                  {category.data.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{item.service}</h4>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                      <div className="flex items-center space-x-4 ml-4">
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600">{item.price}</div>
                          <div className="text-sm text-gray-500">Base rate</div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-red-600">{item.zone1Addition}</div>
                          <div className="text-xs text-gray-500">Zone 1</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            All prices include VAT. Call-out charges cover the first hour of work.
          </p>
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <a
              href="/get-quote"
              className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transform hover:-translate-y-0.5 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Get a Free Quote
            </a>
            <a
              href="tel:+447379787766"
              className="px-6 py-3 bg-white text-primary-600 border-2 border-primary-600 rounded-lg font-semibold hover:bg-primary-50 transform hover:-translate-y-0.5 transition-all duration-300"
            >
              📞 Call for Details
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PriceList;
